import {memo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DockedPanel, useActiveDockedPanel, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {renderActivePanel} from './panel-render-map'
import {ManageBatchOpenTelemetry} from './manage-batch/ManageBatchOpenTelemetry'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './docked-side-panel-constants'

/**
 * Shell for the docked side panel. Mounts exactly the active panel via the
 * render map — inactive panels stay unmounted (lazy mount on open, unmount on
 * close).
 *
 * The Comments panel has a flag-off carve-out: when `updated_pull_request_header`
 * is OFF, the panel is rendered as a child of `OpenCommentsPanelButton` instead
 * (so it inherits `PullRequestStoreProvider` scope) and portals into this target
 * div via `createPortal`. We suppress the render-map render of Comments under
 * that flag to avoid double-mounting.
 */
export const DockedSidePanelContent = memo(function DockedSidePanelContentComponent() {
  return (
    <div id={DOCKED_SIDE_PANEL_CONTENT_ID} className="height-full d-flex flex-column">
      {isFeatureEnabled('unified_batch_pr_comments') && (
        // Own boundary so panel telemetry can never take down the panel itself.
        <ErrorBoundary boundaryName="ManageBatchOpenTelemetry" fallback={null}>
          <ManageBatchOpenTelemetry />
        </ErrorBoundary>
      )}
      <ErrorBoundary boundaryName="DockedSidePanelContent" fallback={null}>
        <DockedSidePanelBody />
      </ErrorBoundary>
    </div>
  )
})

function DockedSidePanelBody() {
  const activeId = useActiveDockedPanel()
  if (!activeId) return null
  return <ActivePanelSlot key={activeId} id={activeId} />
}

function ActivePanelSlot({id}: {id: DockedPanel}) {
  const isOpen = useIsDockedPanelOpen(id)
  if (!isOpen) return null
  if (id === DockedPanel.Comments && !isFeatureEnabled('updated_pull_request_header')) return null
  return renderActivePanel(id)
}
